import type { ReactNode } from "react";
import { SkyButton, SkyText } from "@skypointcloud/skypoint-ui";
import { Link } from "wouter";
import { watchPresetOf, type WatchView } from "@shared/types/notifications";
import { useTenantInstance } from "@/contexts/TenantInstanceContext";
import { useMyWatches, useSetWatch } from "@/hooks/use-alert-routing";
import { useApiErrorNotice } from "@/lib/feedback/useApiErrorNotice";
import { ROUTES } from "@/lib/routes";

/**
 * My alerts, in the user menu and on its own page `/my-alerts` (alert routing, design "UI"; #1271): the signed-in person's own Watches in
 * the selected instance, what each sends, where it goes, and "Stop watching". A Watch is personal,
 * so this lists the caller's only. Security alerts (a TenantAdmin's or PlatformAdmin's own) sit
 * beside it in the menu (`SecurityAlerts.tsx`).
 */

const KIND = { dataflow: "Connection", schedule: "Schedule" } as const;

const OUTCOME_WORD = { failure: "failures", warning: "warnings", success: "successes" } as const;

/** "Failures and warnings", "Every result", or the set itself ("Warnings", "Failures and successes"). */
function sendsText(watch: WatchView): string {
  const preset = watchPresetOf(watch.outcomes);
  if (preset === "every") return "Every result";
  if (preset === "problems") return "Failures and warnings";
  const words = (["failure", "warning", "success"] as const)
    .filter((outcome) => watch.outcomes.includes(outcome))
    .map((outcome) => OUTCOME_WORD[outcome]);
  const text =
    words.length > 1 ? `${words.slice(0, -1).join(", ")} and ${words.at(-1)}` : (words[0] ?? "");
  return text ? text.charAt(0).toUpperCase() + text.slice(1) : "Nothing";
}

/** One watch in the list: its name, what it sends, and "Stop". */
function WatchItem({
  watch,
  setWatch,
  showApiError,
}: {
  watch: WatchView;
  setWatch: ReturnType<typeof useSetWatch>;
  showApiError: ReturnType<typeof useApiErrorNotice>;
}) {
  const name =
    watch.name ?? (watch.targetType === "dataflow" ? "Deleted connection" : "Deleted schedule");
  return (
    <li className="flex items-center gap-2">
      <span className="flex min-w-0 flex-1 flex-col">
        <SkyText size={200} weight="semibold" truncate>
          {name}
        </SkyText>
        <SkyText size={200} color="muted">
          {KIND[watch.targetType]} · {sendsText(watch)}
        </SkyText>
      </span>
      <SkyButton
        appearance="subtle"
        size="small"
        aria-label={`Stop watching ${name}`}
        disabled={setWatch.isPending}
        onClick={() =>
          setWatch.mutate(
            { targetType: watch.targetType, targetId: watch.targetId, outcomes: null },
            {
              onError: (error) => showApiError(error, `We could not stop watching ${name}.`),
            },
          )
        }
      >
        Stop
      </SkyButton>
    </li>
  );
}

/**
 * `menu`: the user menu's section, headed "My alerts in {instance}", with a link to the page.
 * `page` (#1271): the body of `/my-alerts`, whose page header already names it.
 */
export function MyAlerts({
  placement = "menu",
  onOpenPage,
}: {
  placement?: "menu" | "page";
  /** The menu's "Open My alerts" link was chosen (the menu closes). */
  onOpenPage?: () => void;
}) {
  const { selectedInstance } = useTenantInstance();
  const watches = useMyWatches();
  const setWatch = useSetWatch();
  const showApiError = useApiErrorNotice();
  const view = watches.data;

  if (!selectedInstance) return null;

  function renderWatches(): ReactNode {
    if (!view && watches.isError) {
      return (
        <SkyText as="p" size={200} color="muted" testId="my-alerts-error">
          Your alerts did not load. Reload the page to try again.
        </SkyText>
      );
    }
    if (!view) {
      // B-76: a list still loading holds the room of a short list, so nothing under it moves.
      return (
        <div className="min-h-14" data-testid="my-alerts-loading">
          <SkyText size={200} color="muted">
            Loading…
          </SkyText>
        </div>
      );
    }
    if (view.watches.length === 0) {
      return (
        <SkyText as="p" size={200} color="muted" testId="my-alerts-empty">
          You watch nothing here yet. Open a connection or a schedule and choose Watch.
        </SkyText>
      );
    }
    return (
      <>
        <ul className="flex flex-col gap-1" data-testid="my-alerts-list">
          {view.watches.map((watch) => (
            <WatchItem
              key={`${watch.targetType}:${watch.targetId}`}
              watch={watch}
              setWatch={setWatch}
              showApiError={showApiError}
            />
          ))}
        </ul>
        {view.callerEmailMasked ? (
          <SkyText as="p" size={200} color="muted">
            Sent to {view.callerEmailMasked}.
          </SkyText>
        ) : null}
      </>
    );
  }

  if (placement === "page") {
    return (
      <div className="flex flex-col gap-2" data-testid="my-alerts">
        {renderWatches()}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-2 px-4 py-2" data-testid="my-alerts">
      <SkyText as="p" size={200} color="muted" className="uppercase tracking-wide">
        My alerts in {selectedInstance.name}
      </SkyText>
      {renderWatches()}
      <Link
        href={ROUTES.myAlerts}
        data-testid="user-menu-my-alerts"
        onClick={onOpenPage}
        className="self-start rounded-sm text-sm font-medium text-accent-text hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        Open My alerts
      </Link>
    </div>
  );
}
